/**
 * Canonical event-type icon — THE cross-provider standard for the events
 * surface. Icons key off the canonical `EventType.key` (access.granted,
 * alarm.armed, io.on, …), never off vendor strings: techfass 'Valid ID',
 * acre's SPC codes and axema's WS frames all resolve to the same canonical
 * types via VendorEventMap, so one map covers every provider identically.
 * Unknown/unmapped types get the muted help circle.
 *
 * Hand-rolled 24×24 stroke icons (no icon-lib dependency), currentColor,
 * sized by the caller's text size.
 */

type IconProps = { className?: string }

function Svg({ className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`inline-block h-4 w-4 shrink-0 ${className ?? ''}`}
    >
      {children}
    </svg>
  )
}

const PATHS = {
  circleCheck: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </>
  ),
  circleX: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m9 9 6 6M15 9l-6 6" />
    </>
  ),
  keyQuestion: (
    <>
      <circle cx="8" cy="14" r="4" />
      <path d="m11 11 8-8M17 5l2 2M14 8l2 2" />
      <path d="M8 14h.01" />
    </>
  ),
  doorOpen: (
    <>
      <path d="M4 21h16M6 21V5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v16" />
      <path d="M15 12h.01" />
      <path d="M18 8v8" />
    </>
  ),
  shieldCheck: (
    <>
      <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
      <path d="m9.5 12 2 2 3.5-4" />
    </>
  ),
  shieldOff: (
    <>
      <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
      <path d="m9.5 9.5 5 5M14.5 9.5l-5 5" />
    </>
  ),
  bellRing: (
    <>
      <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M10 20a2 2 0 0 0 4 0" />
      <path d="m4 3 2 2M20 3l-2 2" />
    </>
  ),
  toggleOn: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <circle cx="16" cy="12" r="2.5" fill="currentColor" stroke="none" />
    </>
  ),
  toggleOff: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <circle cx="8" cy="12" r="2.5" />
    </>
  ),
  arrowUp: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16V8M8.5 11.5 12 8l3.5 3.5" />
    </>
  ),
  arrowDown: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8.5 12.5 12 16l3.5-3.5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  phone: (
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
  ),
  phoneMissed: (
    <>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
      <path d="m16 4 5 5M21 4l-5 5" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.35-1 .9-1 1.7M12 16.5h.01" />
    </>
  ),
} as const

/** Canonical type → (glyph, tone class). Tones follow the same semantic
 *  coloring as SeverityChip: ok green, danger red, warning amber, muted. */
const ICONS: Record<string, { path: keyof typeof PATHS; tone: string }> = {
  'access.granted': { path: 'circleCheck', tone: 'text-ostrel-ok' },
  'access.denied': { path: 'circleX', tone: 'text-ostrel-danger' },
  'access.unknown_credential': { path: 'keyQuestion', tone: 'text-ostrel-gold' },
  'door.forced': { path: 'doorOpen', tone: 'text-ostrel-danger' },
  'door.held': { path: 'doorOpen', tone: 'text-ostrel-gold' },
  'alarm.triggered': { path: 'bellRing', tone: 'text-ostrel-danger' },
  'alarm.armed': { path: 'shieldCheck', tone: 'text-ostrel-navy' },
  'alarm.disarmed': { path: 'shieldOff', tone: 'text-ostrel-ink-muted' },
  'io.on': { path: 'toggleOn', tone: 'text-ostrel-navy' },
  'io.off': { path: 'toggleOff', tone: 'text-ostrel-ink-muted' },
  'system.online': { path: 'arrowUp', tone: 'text-ostrel-ok' },
  'system.offline': { path: 'arrowDown', tone: 'text-ostrel-gold' },
  'system.notice': { path: 'info', tone: 'text-ostrel-ink-muted' },
  'intercom.call': { path: 'phone', tone: 'text-ostrel-navy' },
  'intercom.call_missed': { path: 'phoneMissed', tone: 'text-ostrel-gold' },
  unknown: { path: 'help', tone: 'text-ostrel-ink-muted' },
}

/** The icon for one canonical event type key (e.g. `access.granted`). */
export function EventIcon({ type, className }: { type: string; className?: string }) {
  const icon = ICONS[type] ?? ICONS.unknown
  return (
    <Svg className={`${icon.tone} ${className ?? ''}`}>
      {PATHS[icon.path]}
    </Svg>
  )
}
